import React, { useEffect, useState } from 'react';
import { MutationStatus, useQueryClient, UseQueryResult } from 'react-query';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { UseFormReturn } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { prepareFiles } from 'web-library';
import { configQuery } from '../../../api/config';
import { CONFIG_KEY_GET_CONFIG } from '../../../api/config/key';
import { ServiceError } from '../../../hooks/error';
import { ReactQuertReturnExtra } from '../../../hooks/query';
import { configNameDic, Configs } from '../../../models';
import { usePostSystemSettingsRequest } from '../../../api-refactored/system-settings/usePostSystemSettingsRequest';
import { usePostRequestFilePresigned } from '../../../api-refactored/user-group-access-configuration/usePostRequestFilePresigned';
import { UpdateConfigRequest } from '../../../api/config/model';

export interface AdminConfigurationReturn {
  handleConfirmChangesModal: () => void;
  result: UseQueryResult<Configs, ServiceError> & ReactQuertReturnExtra;
}

export interface useAdminConfigurationProps {
  formHook: UseFormReturn;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setUploadFileErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  setConfigUpdateSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenConfirmChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useAdminConfiguration = ({
  formHook,
  files,
  setFiles,
  setUploadFileErrorMessage,
  setApiButtonStatus,
  setErrorMessage,
  setConfigUpdateSuccess,
  setOpenConfirmChangesModal,
}: useAdminConfigurationProps): AdminConfigurationReturn => {
  const queryClient = useQueryClient();
  const result = configQuery.useConfigQuery();

  const { uuid } = useParams<{ uuid: string }>();

  const [shouldSubmit, setShouldSubmit] = useState<boolean>(false);

  const history = useHistory();

  const { mutate: mutatePostSystemSettingsRequest } = usePostSystemSettingsRequest({
    onSuccess: () => {
      setShouldSubmit(false);
      setErrorMessage('');
      setApiButtonStatus('success');
      queryClient.invalidateQueries(CONFIG_KEY_GET_CONFIG).then(() => {
        setConfigUpdateSuccess(true);
        setTimeout(() => setConfigUpdateSuccess(false), 3000);
      });
      history.go(0);
    },
    onError: (err) => {
      setApiButtonStatus('error');
      setErrorMessage(err?.response?.data.message!);
      setShouldSubmit(false);
    },
  });

  const { mutate: mutatePostRequestFilePresigned } = usePostRequestFilePresigned({
    onSuccess: (data) => {
      const tempFiles: UploadedFile[] = [];
      data.forEach((_value, index) => {
        const pendingFile = files[index];
        const fileData = data[index];
        pendingFile.presignedUrl = fileData.presignedUrl;
        pendingFile.key = fileData.key;
        tempFiles.push(pendingFile);
      });
      setFiles(tempFiles);
      setUploadFileErrorMessage('');
    },
    onError: (err) => {
      setUploadFileErrorMessage(err?.response?.data.message!);
      setApiButtonStatus('error');
      setShouldSubmit(false);
    },
  });

  const { getValues } = formHook;

  useEffect(() => {
    const isAllFilesUploaded = files.every((f) => f.uploaded);
    const fileUploadHasError = files.some((file) => file.hasError);
    const fileKeys: string[] = [''];
    files.forEach((f) => {
      fileKeys.push(f.key as string);
    });
    console.log('fileUploadHasError', fileUploadHasError);

    if (fileUploadHasError) {
      setApiButtonStatus('error');
      setShouldSubmit(false);
    }
    if (isAllFilesUploaded && shouldSubmit) {
      const configData = result?.data;
      const dataToPost: UpdateConfigRequest[] = [];
      // only submit changed item
      const formData = {
        daysToDisableAccount: getValues('daysToDisableAccount'),
        daysToLockInactivityAccount: getValues('daysToLockInactivityAccount'),
        daysToEmailAdmin: getValues('daysToEmailAdmin'),
        daysToInvalidateLink: getValues('daysToInvalidateLink'),
        timesOfMaxLogin: getValues('timesOfMaxLogin'),
        minutesOfSessionTimeout: getValues('minutesOfSessionTimeout'),
        minutesOfSessionTimeoutPrivilegedAdmin: getValues('minutesOfSessionTimeoutPrivilegedAdmin'),
        minutesOfSessionTimeoutNonPriviledgeAdmin: getValues(
          'minutesOfSessionTimeoutNonPriviledgeAdmin'
        ),
        daysOfPaymentLinkExpire: getValues('daysOfPaymentLinkExpire'),
        daysOfAccessLinkExpire: getValues('daysOfAccessLinkExpire'),
        timesOfMaxFailedAttempt: getValues('timesOfMaxFailedAttempt'),
        secondToWaitLogin: getValues('secondToWaitLogin'),
      };

      Object.keys(formData).forEach((_k, i) => {
        const oneData = Object.values(formData)[i] as string;
        const oneConfigData = Object.values(configData as Configs)[i]?.value;
        if (oneData !== oneConfigData && oneConfigData) {
          dataToPost.push({
            key: Object.values(configNameDic)[i],
            value: oneData,
          });
        }
        return null;
      });
      mutatePostSystemSettingsRequest({
        updateConfigModels: dataToPost,
        requestCreationModel: {
          requestorRemarks: getValues('remarks'),
          approverUuid: getValues('approver')?.code,
          fileKeys,
        },
      });
    }
  }, [shouldSubmit, files, result?.data, mutatePostSystemSettingsRequest, getValues]);

  const handleConfirmChangesModal = () => {
    setOpenConfirmChangesModal(false);
    const { filenames } = prepareFiles(files, setFiles);
    setShouldSubmit(true);
    setApiButtonStatus('loading');

    if (filenames.length) {
      mutatePostRequestFilePresigned({
        filenames,
        uuid,
      });
    }
  };

  return {
    result,
    handleConfirmChangesModal,
  };
};
